import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MapPin, Clock, Briefcase, ChevronRight, Users, Heart, Zap, Globe, Loader2, Settings, X, Upload, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const Careers = ({ onBackToHome, onAdminClick }) => {
  const { t, i18n } = useTranslation();
  const [jobOpenings, setJobOpenings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedIn: '',
    coverLetter: '',
    resumeUrl: ''
  });

  useEffect(() => {
    fetchJobPositions();
  }, [i18n.language]);

  const fetchJobPositions = async () => {
    try {
      const response = await fetch(apiUrl(`careers/positions?lang=${i18n.language}`));
      if (response.ok) {
        const data = await response.json();
        setJobOpenings(data);
      }
    } catch (error) {
      logger.error('Error fetching job positions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setShowApplicationModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(i18n.language === 'tr' ? 'Lütfen PDF veya Word dosyası yükleyin' : 'Please upload a PDF or Word document');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(i18n.language === 'tr' ? 'Dosya boyutu 10MB\'dan az olmalıdır' : 'File size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await fetch(apiUrl("upload"), {
        method: 'POST',
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        const fileUrl = `${API_URL}${data.url}`;
        setFormData(prev => ({ ...prev, resumeUrl: fileUrl }));
        toast.success(i18n.language === 'tr' ? 'CV yüklendi!' : 'Resume uploaded!');
      } else {
        toast.error(i18n.language === 'tr' ? 'Yükleme başarısız' : 'Upload failed');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error(i18n.language === 'tr' ? 'Yükleme başarısız' : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(apiUrl("career-applications"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          positionId: selectedJob.id,
          positionTitle: selectedJob.title
        })
      });

      if (response.ok) {
        toast.success(i18n.language === 'tr' 
          ? 'Başvurunuz alındı! En kısa sürede size dönüş yapacağız.' 
          : 'Application submitted! We\'ll get back to you soon.');
        setShowApplicationModal(false);
        setFormData({ fullName: '', email: '', phone: '', linkedIn: '', coverLetter: '', resumeUrl: '' });
        setSelectedJob(null);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      logger.error('Error:', error);
      toast.error(i18n.language === 'tr' ? 'Bir hata oluştu. Lütfen tekrar deneyin.' : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = i18n.language === 'tr' ? [
    { icon: Globe, title: "Her Yerden Çalışın", description: "Tamamen dağıtık ekibimize katılın ve en verimli olduğunuz yerden çalışın" },
    { icon: Users, title: "İşbirlikçi Kültür", description: "Liderlik gelişimini dönüştürmeye adanmış tutkulu profesyonellerle çalışın" },
    { icon: Heart, title: "Etki Yaratın", description: "Her gün liderlerin ve organizasyonların tam potansiyellerine ulaşmalarına yardımcı olun" },
    { icon: Zap, title: "Büyüme Fırsatları", description: "Sürekli öğrenme ve profesyonel gelişim bizim özümüzdedir" }
  ] : [
    { icon: Globe, title: "Work From Anywhere", description: "Join our fully distributed team and work from wherever you're most productive" },
    { icon: Users, title: "Collaborative Culture", description: "Work with passionate professionals dedicated to transforming leadership development" },
    { icon: Heart, title: "Make an Impact", description: "Help leaders and organizations reach their full potential every day" },
    { icon: Zap, title: "Growth Opportunities", description: "Continuous learning and professional development are at our core" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={onBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors" data-testid="back-to-home-btn">
            <ArrowLeft className="w-5 h-5" />
            <span>{i18n.language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}</span>
          </button>
          {onAdminClick && (
            <button onClick={onAdminClick} className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors text-sm" data-testid="careers-admin-btn">
              <Settings className="w-4 h-4" />
              <span>{i18n.language === 'tr' ? 'Yönetici' : 'Admin'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="careers-title">{t('pages.careers.title')}</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">{t('pages.careers.subtitle')}</p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">{i18n.language === 'tr' ? 'Neden Bizimle Çalışmalısınız' : 'Why Work With Us'}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={value.title || `value-${index}`} className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">{i18n.language === 'tr' ? 'Açık Pozisyonlar' : 'Open Positions'}</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">{i18n.language === 'tr' ? 'Mevcut açık pozisyonlarımızı keşfedin ve bir sonraki fırsatınızı bulun' : 'Explore our current openings and find your next opportunity'}</p>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : jobOpenings.length > 0 ? (
            <div className="max-w-4xl mx-auto space-y-6">
              {jobOpenings.map((job) => (
                <div key={job.id} className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow" data-testid={`job-card-${job.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.department}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{job.type}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">{job.description}</p>
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">{i18n.language === 'tr' ? 'Gereksinimler:' : 'Requirements:'}</h4>
                    <ul className="space-y-2">
                      {job.requirements.map((req, index) => (
                        <li key={req} className="flex items-start gap-2 text-gray-600">
                          <ChevronRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleApplyClick(job)} data-testid={`apply-btn-${job.id}`}>
                    {t('pages.careers.apply')}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl max-w-2xl mx-auto">
              <p className="text-gray-600 text-lg mb-4">{t('pages.careers.noPositions')}</p>
              <p className="text-gray-500">
                {i18n.language === 'tr' ? 'CV\'nizi şu adrese gönderin: ' : 'Send your CV to '}
                <a href="mailto:bilgi@coachmeupp.com" className="text-blue-600 hover:underline">bilgi@coachmeupp.com</a>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{i18n.language === 'tr' ? 'Uygun Pozisyon Bulamadınız mı?' : 'Don\'t See a Fit?'}</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">{i18n.language === 'tr' ? 'Her zaman yetenekli bireyler arıyoruz. Özgeçmişinizi gönderin ve misyonumuza nasıl katkıda bulunabileceğinizi bize bildirin.' : 'We\'re always looking for talented individuals. Send us your resume and let us know how you can contribute to our mission.'}</p>
          <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'mailto:bilgi@coachmeupp.com?subject=General Application'} data-testid="general-apply-btn">
            {i18n.language === 'tr' ? 'Özgeçmişinizi Gönderin' : 'Send Your Resume'}
          </Button>
        </div>
      </section>

      {/* Application Modal */}
      {showApplicationModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{i18n.language === 'tr' ? 'Başvuru Formu' : 'Application Form'}</h2>
                <p className="text-gray-500 text-sm mt-1">{selectedJob.title}</p>
              </div>
              <button onClick={() => setShowApplicationModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitApplication} className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName">{i18n.language === 'tr' ? 'Ad Soyad' : 'Full Name'} *</Label>
                  <Input id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required placeholder={i18n.language === 'tr' ? 'Adınız Soyadınız' : 'Your full name'} className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="email">{i18n.language === 'tr' ? 'E-posta' : 'Email'} *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="you@example.com" className="mt-2" />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">{i18n.language === 'tr' ? 'Telefon' : 'Phone'}</Label>
                  <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+90 5XX XXX XX XX" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="linkedIn">LinkedIn</Label>
                  <Input id="linkedIn" name="linkedIn" value={formData.linkedIn} onChange={handleInputChange} placeholder="https://linkedin.com/in/..." className="mt-2" />
                </div>
              </div>

              <div>
                <Label>{i18n.language === 'tr' ? 'CV / Özgeçmiş' : 'Resume / CV'}</Label>
                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} className="hidden" data-testid="resume-upload" />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      <span className="text-sm text-gray-500">{i18n.language === 'tr' ? 'Yükleniyor...' : 'Uploading...'}</span>
                    </div>
                  ) : formData.resumeUrl ? (
                    <div className="flex flex-col items-center gap-2 text-green-600">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">{i18n.language === 'tr' ? 'CV Yüklendi ✓' : 'Resume Uploaded ✓'}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-600">{i18n.language === 'tr' ? 'CV yüklemek için tıklayın' : 'Click to upload resume'}</span>
                      <span className="text-xs text-gray-400">PDF, DOC, DOCX (max 10MB)</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="coverLetter">{i18n.language === 'tr' ? 'Ön Yazı' : 'Cover Letter'}</Label>
                <Textarea id="coverLetter" name="coverLetter" value={formData.coverLetter} onChange={handleInputChange} rows={5} placeholder={i18n.language === 'tr' ? 'Kendinizi ve bu pozisyona neden başvurduğunuzu anlatın...' : 'Tell us about yourself and why you\'re interested in this position...'} className="mt-2" />
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <Button type="button" variant="outline" onClick={() => setShowApplicationModal(false)}>{i18n.language === 'tr' ? 'İptal' : 'Cancel'}</Button>
                <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{i18n.language === 'tr' ? 'Gönderiliyor...' : 'Submitting...'}</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />{i18n.language === 'tr' ? 'Başvuruyu Gönder' : 'Submit Application'}</>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Careers;
